/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import {
  ApplicationError,
  ChangedEvent,
  formatValue,
  ObjectAdapter,
  parseValue,
  validate,
  ValidationError,
  ValidationRules,
  Formatter,
  Format,
  CoreErrorKey
} from '@typescript-kit/core';
import { Observable, Subscription } from 'rxjs';
import { ViewComponentKey } from '../key/view-component.key';
import { ViewErrorKey } from '../key/view-error.key';
import { ComponentData, ComponentModel, ComponentFieldKey } from '../model/component.model';
import { Alignment } from '../model/alignment.model';
import { ContextBinding, ContextBindings } from '../model/context-binding.model';

export interface ValueData extends ComponentData {
  scope?: string;
  value?: any;
  formattedValue?: string;
  format?: string | Format | { format?: (value: any) => string, parse?: (value: string) => any };
  formatOriginal?: boolean;  // re-format formatted value
  formatError?: ApplicationError;
  alignment?: Alignment;
  // prefix?: string | ComponentData;
  suffix?: string | ComponentData;
  context?: object;
  contextBinding?: ContextBinding;
  validation?: ValidationRules;
  validationError?: ApplicationError;
  isValid?: boolean;
  onValueChanged?: (value: any, originalValue: any, model: ValueModel) => void;
}

export class ValueFieldKey {
  static readonly SCOPE = 'scope';
  static readonly VALUE = 'value';
  static readonly FORMATTED_VALUE = 'formattedValue';
  static readonly FORMAT = 'format';
  static readonly FORMAT_ERROR = 'formatError';
  static readonly FORMAT_ORIGINAL = 'formatOriginal';
  static readonly ALIGNMENT = 'alignment';
  // static readonly PREFIX = 'prefix';
  static readonly SUFFIX = 'suffix';
  static readonly CONTEXT = 'context';
  static readonly CONTEXT_BINDING = 'contextBinding';
  static readonly VALIDATION = 'validation';
  static readonly VALIDATION_ERROR = 'validationError';
  static readonly IS_VALID = 'isValid';
  static readonly ON_VALUE_CHANGED = 'onValueChanged';
}

export class ValueModel extends ComponentModel implements ValueData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(ValueFieldKey).map((key) => ValueFieldKey[key])
  );
  private contextChangedSubscription: Subscription;
  private validationPromise: Promise<ValidationError[]> = null;
  private validationResolver: Array<(error: ApplicationError) => void> = null;

  constructor(data?: ValueData, keys?: string[]) {
    super(data, keys || ValueModel.KEYS);
  }

  get scope(): string {
    return this.get(ValueFieldKey.SCOPE);
  }

  set scope(value: string) {
    this.set(ValueFieldKey.SCOPE, value);
  }

  get value(): any {
    return this.get(ValueFieldKey.VALUE);
  }

  set value(value: any) {
    this.set(ValueFieldKey.VALUE, value);
  }

  get formattedValue(): string {
    return this.get(ValueFieldKey.FORMATTED_VALUE, '');
  }

  set formattedValue(value: string) {
    this.set(ValueFieldKey.FORMATTED_VALUE, value);
  }

  get format(): string | Format | Formatter {
    return this.get(ValueFieldKey.FORMAT);
  }

  set format(value: string | Format | Formatter) {
    this.set(ValueFieldKey.FORMAT, value);
  }

  get formatError(): ApplicationError {
    return this.get(ValueFieldKey.FORMAT_ERROR);
  }

  set formatError(value: ApplicationError) {
    this.set(ValueFieldKey.FORMAT_ERROR, value);
  }

  get formatOriginal(): boolean {
    return this.get(ValueFieldKey.FORMAT_ORIGINAL);
  }

  set formatOriginal(value: boolean) {
    this.set(ValueFieldKey.FORMAT_ORIGINAL, value);
  }

  get alignment(): Alignment {
    return this.get(ValueFieldKey.ALIGNMENT);
  }

  set alignment(value: Alignment) {
    this.set(ValueFieldKey.ALIGNMENT, value);
  }

  // get prefix(): string | ComponentModel {
  //   return this.get(ValueFieldKey.PREFIX);
  // }

  // set prefix(value: string | ComponentModel) {
  //   this.set(ValueFieldKey.PREFIX, value);
  // }

  get suffix(): string | ComponentModel {
    return this.get(ValueFieldKey.SUFFIX);
  }

  set suffix(value: string | ComponentModel) {
    this.set(ValueFieldKey.SUFFIX, value);
  }

  get context(): object {
    return this.get(ValueFieldKey.CONTEXT);
  }

  set context(value: object) {
    this.set(ValueFieldKey.CONTEXT, value);
  }

  get contextBinding(): ContextBinding {
    return this.get(ValueFieldKey.CONTEXT_BINDING);
  }

  set contextBinding(value: ContextBinding) {
    this.set(ValueFieldKey.CONTEXT_BINDING, value);
  }

  get validation(): ValidationRules {
    return this.get(ValueFieldKey.VALIDATION);
  }

  set validation(value: ValidationRules) {
    this.set(ValueFieldKey.VALIDATION, value);
  }

  get validationError(): ApplicationError {
    return this.get(ValueFieldKey.VALIDATION_ERROR);
  }

  set validationError(value: ApplicationError) {
    this.set(ValueFieldKey.VALIDATION_ERROR, value);
  }

  get isValid(): boolean {
    return this.get(ValueFieldKey.IS_VALID);
  }

  get onValueChanged(): (value: any, originalValue: any, model: ValueModel) => void {
    return this.get(ValueFieldKey.ON_VALUE_CHANGED);
  }

  async validate(): Promise<boolean> {
    const value = this.value;
    const key = this.key;
    const scope = this.scope;
    const context = this.context;
    const validationPromise = validate(this.validation || false, value, key, scope, context);
    this.validationPromise = validationPromise;
    return validationPromise
      .then((validationErrors: ValidationError[]) => {
        if (!validationErrors) {
          return null;
        }
        return new ApplicationError({
          key: ViewErrorKey.VALIDATION,
          arguments: { value, key, scope, context, errors: validationErrors }
        });
      })
      .catch((error: ApplicationError) => error)
      .then((validationError: ApplicationError) => {
        if (validationPromise !== this.validationPromise) {
          // validation result is outdated
          return this.resolveValidationError()
            .then((resolvedError) => {
              return resolvedError && resolvedError.key !== ViewErrorKey.VALIDATION
                ? undefined
                : !resolvedError;
            });
        }
        const validationResolver = this.validationResolver;
        this.validationPromise = null;
        this.validationResolver = null;
        this.validationError = validationError;
        if (validationResolver) {
          validationResolver.forEach((resolver) => resolver(validationError));
        }
        if (validationError && validationError.key !== ViewErrorKey.VALIDATION) {
          throw validationError;
        }
        return !validationError;
      });
  }

  async resolveIsValid(): Promise<void> {
    const validationError = await this.resolveValidationError();
    if (!validationError) {
      return;
    }
    throw validationError;
  }

  async resolveValidationError(): Promise<ApplicationError> {
    if (this.validationPromise) {
      // is validating
      return new Promise<ApplicationError>((resolve) => {
        if (this.validationResolver) {
          this.validationResolver.push(resolve);
        } else {
          this.validationResolver = [resolve];
        }
      });
    }
    if (this.validationError === undefined) {
      // validation was never triggered
      try {
        await this.validate();
      } catch (error) {
        // DO NOTHING
      }
    }
    return this.validationError;
  }

  protected setupValues(values: ValueData, exclusive: boolean): ValueData {
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.VALUE;
    }
    this.setupContextBinding(values, exclusive);
    this.setupFormatting(values, exclusive);
    this.setupValidation(values, exclusive);
    this.setupIsValid(values, exclusive);
    return super.setupValues(values, exclusive) as ValueData;
  }

  protected setupContextBinding(values: ValueData, exclusive: boolean): void {
    if (!exclusive
      && !(ValueFieldKey.CONTEXT in values)
      && !(ValueFieldKey.CONTEXT_BINDING in values)
      && !(ComponentFieldKey.KEY in values)
    ) {
      return;
    }
    if (exclusive && values.contextBinding === undefined) {
      values.contextBinding = ContextBindings.NONE;
    }
    const key: string = this.getSetupValue(values, ComponentFieldKey.KEY, exclusive);
    const context: object = this.getSetupValue(values, ValueFieldKey.CONTEXT, exclusive);
    const contextBinding: ContextBinding = this.getSetupValue(values, ValueFieldKey.CONTEXT_BINDING, exclusive);
    if (this.context === context && this.contextBinding === contextBinding && this.key === key) {
      return;
    }
    if (this.contextChangedSubscription) {
      this.contextChangedSubscription.unsubscribe();
      this.contextChangedSubscription = null;
    }
    if (!context || contextBinding === ContextBindings.NONE) {
      return;
    }
    if (!key) {
      throw new ApplicationError({
        key: ViewErrorKey.COMPONENT_KEY_MISSING,
        message: 'Component key missing'
      });
    }
    // if (!context) {
    //   throw new ApplicationError({
    //     key: ViewErrorKey.COMPONENT_CONTEXT_MISSING,
    //     message: 'Component context missing'
    //   });
    // }
    if (typeof context !== 'object') {
      throw new ApplicationError({
        key: ViewErrorKey.COMPONENT_CONTEXT_INVALID,
        arguments: { context },
        message: 'Component context invalid'
      });
    }
    switch (contextBinding) {
      case ContextBindings.MODEL_TO_CONTEXT:
        break;
      case ContextBindings.CONTEXT_TO_MODEL:
      case ContextBindings.TWO_WAY:
        const changedEvent: Observable<ChangedEvent> = context['changedEvent'];
        if (!changedEvent || !(changedEvent instanceof Observable)) {
          throw new ApplicationError({
            key: ViewErrorKey.COMPONENT_CONTEXT_INVALID,
            arguments: { context },
            message: 'Component context invalid'
          });
        }
        this.contextChangedSubscription = changedEvent.subscribe((event: ChangedEvent) => {
          if (key in event.changes) {
            this.value = event.changes[key].currentValue;
          }
        });
        if (contextBinding === ContextBindings.CONTEXT_TO_MODEL
          || !(ValueFieldKey.VALUE in values) && !(ValueFieldKey.FORMATTED_VALUE in values)) {
          values.value = ObjectAdapter.from(context).get(key);
        }
        break;
      default:
        throw new ApplicationError({
          key: ViewErrorKey.COMPONENT_CONTEXT_BINDING_INVALID,
          arguments: { contextBinding },
          message: `Component context binding '${contextBinding}' invalid`
        });
    }
  }

  protected setupFormatting(values: ValueData, exclusive: boolean): void {
    if (exclusive && !(ValueFieldKey.FORMAT_ORIGINAL in values)) {
      values.formatOriginal = true;
    }
    const format = this.getSetupValue(values, ValueFieldKey.FORMAT, exclusive);
    let isParsedValue = false;
    if (!format) {
      // no format
      if (exclusive || (ValueFieldKey.FORMAT in values && values.format !== this.format)) {
        if (!(ValueFieldKey.FORMAT_ERROR in values)) {
          // format was cleared => clear possible parse/format error
          values.formatError = null;
        }
      }
      if (ValueFieldKey.FORMATTED_VALUE in values) {
        if (!(ValueFieldKey.VALUE in values)) {
          values.value = values.formattedValue;
        }
      } else {
        if (ValueFieldKey.VALUE in values || ValueFieldKey.FORMAT in values) {
          const value = this.getSetupValue(values, ValueFieldKey.VALUE, exclusive);
          values.formattedValue = ValueModel.toFormattedValue(value);
        }
      }
    } else {
      if (ValueFieldKey.FORMATTED_VALUE in values) {
        if (ValueFieldKey.VALUE in values) {
          // clear possible parse/format error
          values.formatError = null;
        } else {
          try {
            if (typeof format === 'string') {
              values.value = parseValue(values.formattedValue, format);
            } else if (format.formatterKey) {
              values.value = parseValue(values.formattedValue, format.formatterKey, format.formatterOptions);
            } else if (typeof format.parse === 'function') {
              values.value = format.parse(values.formattedValue);
            } else {
              const formatterKey = format.toString();
              throw new ApplicationError({
                key: CoreErrorKey.FORMATTER_NOT_FOUND,
                arguments: { formatterKey },
                message: `Formatter '${formatterKey}' not found`
              });
            }
            values.formatError = null;
          } catch (error) {
            values.value = undefined;
            values.formatError = error;
          }
          isParsedValue = true;
        }
      }
      if (!values.formatError && (
        // changed value
        (ValueFieldKey.VALUE in values && values.value !== this.value) ||
        // changed format
        (ValueFieldKey.FORMAT in values && values.format !== this.format) ||
        // re-format parsed value
        (isParsedValue && this.getSetupValue(values, ValueFieldKey.FORMAT_ORIGINAL, exclusive))
      )) {
        // no parse error
        const value = this.getSetupValue(values, ValueFieldKey.VALUE, exclusive);
        try {
          if (typeof format === 'string') {
            values.formattedValue = formatValue(value, format);
          } else if (format.formatterKey) {
            values.formattedValue = formatValue(value, format.formatterKey, format.formatterOptions);
          } else if (typeof format.format === 'function') {
            values.formattedValue = format.format(value);
          } else {
            const formatterKey = format.toString();
            throw new ApplicationError({
              key: CoreErrorKey.FORMATTER_NOT_FOUND,
              arguments: { formatterKey },
              message: `Formatter '${formatterKey}' not found`
            });
          }
          values.formatError = null;
        } catch (error) {
          values.formattedValue = ValueModel.toFormattedValue(value);
          values.formatError = error;
        }
      }
    }
  }

  protected setupValidation(values: ValueData, exclusive: boolean): void {
    if (exclusive || ValueFieldKey.VALUE in values || ValueFieldKey.VALIDATION in values) {
      const validation = this.getSetupValue(values, ValueFieldKey.VALIDATION, exclusive);
      if (!validation) {
        if (!(ValueFieldKey.VALIDATION_ERROR in values)) {
          values.validationError = null;
        }
      } else {
        const value = this.getSetupValue(values, ValueFieldKey.VALUE, exclusive);
        if (value !== this.value || validation !== this.validation) {
          // either value or validation is changed -> clear original validation error
          values.validationError = undefined;
        }
      }

    }
  }

  protected setupIsValid(values: ValueData, exclusive: boolean): void {
    if (exclusive || ValueFieldKey.FORMAT_ERROR in values || ValueFieldKey.VALIDATION_ERROR in values) {
      const formatError = this.getSetupValue(values, ValueFieldKey.FORMAT_ERROR, exclusive);
      if (formatError) {
        values.isValid = false;
        return;
      }
      const validationError = this.getSetupValue(values, ValueFieldKey.VALIDATION_ERROR, exclusive);
      if (validationError) {
        values.isValid = false;
        return;
      }
      values.isValid = validationError === null ? true : undefined;
    }
  }

  protected fireChangedEvent(changedEvent: ChangedEvent): void {
    const changes = changedEvent.changes;
    const valueChange = changes[ValueFieldKey.VALUE];
    if (this.validation && (valueChange
      || changes[ValueFieldKey.SCOPE]
      || changes[ValueFieldKey.CONTEXT]
      || changes[ComponentFieldKey.KEY]) || changes[ValueFieldKey.VALIDATION]
    ) {
      // noinspection JSIgnoredPromiseFromCall
      this.validate();
    }
    if (this.context && (this.contextBinding === ContextBindings.TWO_WAY || this.contextBinding === ContextBindings.MODEL_TO_CONTEXT)) {
      if (valueChange
        || changes[ValueFieldKey.CONTEXT]
        || changes[ValueFieldKey.CONTEXT_BINDING]
        || changes[ComponentFieldKey.KEY]
      ) {
        ObjectAdapter.from(this.context).set(this.key, this.value);
      }
    }
    if (valueChange && this.onValueChanged) {
      this.onValueChanged(valueChange.currentValue, valueChange.originalValue, this);
    }
    super.fireChangedEvent(changedEvent);
  }

  private static toFormattedValue(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value.toString();
  }

}
